import React, { useCallback, useMemo } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { lastOfferLetter } from "../../../../selectors";
import CandidateCard from "../../../../../../components/CandidateCard";
import { hasTest } from "../../selectors";
import useEmailInvite from "../../../../../../hooks/custom/useEmailInvite";
import { getScreeningTag } from "../../../../../../utils/candidates";

import { getOfferStatus, OfferDescription } from "./OfferBadge";

export const statusColor = {
  FINISHED: "#3d64ff",
  PENDING: "#ffaa55",
  ERROR: "#ff4d4e",
  UNRECOGNIZED_ANSWER: "#5725ef"
};

const getTuReciboFeature = company =>
  R.path(["features", "integrations", "tuRecibo"], company) ||
  R.path(["features", "tuRecibo"], company) ||
  {};

const isTeamOfferLetterEnabled = (company, teamId) => {
  if (!teamId) return true;

  const teamOverride = R.path(["teamOverrides", teamId], getTuReciboFeature(company));
  return R.propOr(true, "offerLetterEnabled", teamOverride);
};

const ProcessCard = ({
  data,
  step,
  showOffer,
  isLoading,
  onRemove,
  canMove,
  canChat,
  chatbotStatus,
  chatbotTags = [],
  unreadMessages,
  compact,
  company,
  opportunity,
  isSelected,
  onSelect,
  stepIsDisabled
}) => {
  const { t } = useTranslation();
  const opportunityTeamId =
    R.path(["selected", "team"], opportunity) || R.prop("team", opportunity);
  const isOfferLetterEnabled =
    R.path(["features", "offerLetter", "enabled"], company) !== false &&
    isTeamOfferLetterEnabled(company, opportunityTeamId);

  // TODO change propOr to prop
  const isRegistered = useMemo(() => R.propOr(true, "isRegistered")(data), [
    data
  ]);

  const [inviteCandidate, inviteCandidateStatus] = useEmailInvite(
    company.id,
    opportunity.selected.id
  );

  const history = useHistory();
  const route = useRouteMatch();

  const { id, offerletter } = data;
  const lastOffer = useMemo(() => lastOfferLetter(offerletter || []), [
    offerletter
  ]);

  // TODO this is temporary, please check later if the step is exclusive or not.
  const isWorcketExclusiveStep = useMemo(
    () => R.propSatisfies(R.gt(R.__, 0), "order", step),
    [step]
  );

  const allChatbotTags = useMemo(
    () =>
      R.pipe(
        R.defaultTo([]),
        R.map(R.propOr([], "chatbotTags")),
        R.flatten
      )(chatbotTags),
    [chatbotTags]
  );

  const [status, highlight, statusTooltip] = useMemo(() => {
    return R.cond([
      [
        R.allPass([
          R.propEq("isRegistered", false),
          R.propEq("isWorcketExclusiveStep", true)
        ]),
        () => [
          t("opportunity:process.candidateStatus.registrationPending"),
          statusColor.PENDING,
          t("opportunity:process.candidateStatus.registrationPendingTooltip")
        ]
      ],
      [
        R.either(
          R.pathEq(["step", "type"], "test-gh"),
          R.pathEq(["step", "type"], "test")
        ),
        ({ step, match }) =>
          hasTest(step, match)
            ? [
                t("opportunity:process.candidateStatus.testCompleted"),
                statusColor.FINISHED
              ]
            : [
                t("opportunity:process.candidateStatus.testPending"),
                statusColor.PENDING
              ]
      ],
      [
        R.prop("chatbotStatus"),
        ({ chatbotStatus }) => [
          t(
            `opportunity:process.stepTypes.chatbot.chatbotStatus.${chatbotStatus}`
          ),
          statusColor[chatbotStatus]
        ]
      ],
      [
        R.prop("lastOffer"),
        ({ lastOffer }) => {
          const { status, color } = getOfferStatus(lastOffer);
          const description = <OfferDescription lastOffer={lastOffer} />;
          return [status, color, description];
        }
      ],
      [
        ({ step }) => step.name === "matches",
        ({ match }) => {
          const { screeningText, screeningColor } = getScreeningTag(
            t,
            match.screeningStatus
          );
          const status = screeningText;
          const color = screeningColor;
          return [status, color, ""];
        }
      ],
      [R.T, R.always([])]
    ])({
      step,
      match: data,
      chatbotStatus,
      lastOffer,
      isRegistered,
      isWorcketExclusiveStep
    });
  }, [
    step,
    data,
    chatbotStatus,
    lastOffer,
    isRegistered,
    isWorcketExclusiveStep,
    t
  ]);

  // TODO define invite vía email
  // const sendEmail = () => {
  // };

  const actions = useMemo(() => {
    let cardActions = [];
    if (canMove) {
      if (canChat && isRegistered) {
        cardActions.push({
          name: t("opportunity:process.candidateDetails.canChat"),
          icon: "message",
          onClick: () => history.push(`${route.url}/${id}/chat`),
          badge: unreadMessages,
          "data-test": "chat",
          iconColor: "#747c84",
          hoverColor: "#747c84"
        });
      }

      if (!isRegistered) {
        cardActions.push({
          name: t("opportunity:process.candidateDetails.canInvite"),
          icon:
            inviteCandidateStatus === "loading" ||
            data.inviteCandidateStatus === "loading"
              ? "loading"
              : "mail",
          onClick: () => inviteCandidate(id),
          "data-test": "invite",
          iconColor: "#747c84",
          hoverColor: "#3d64ff",
          disabled:
            inviteCandidateStatus === "loading" ||
            data.inviteCandidateStatus === "loading"
        });
      }

      if (onRemove) {
        cardActions.push({
          name: t("components:CandidateCard.reject"),
          onClick: onRemove,
          loading: isLoading,
          confirm: t("opportunity:process.candidateDetails.confirmRemove"),
          icon: "minus-circle",
          color: "error",
          iconColor: "#747c84",
          hoverColor: "#ffaa55",
          "data-test": "remove"
        });
      }
    }

    return cardActions;
  }, [
    canMove,
    canChat,
    isRegistered,
    onRemove,
    t,
    unreadMessages,
    history,
    route.url,
    id,
    inviteCandidateStatus,
    data.inviteCandidateStatus,
    inviteCandidate,
    isLoading
  ]);

  const handleSelectCandidate = useCallback(
    () => history.push(`${route.url}/${id}/info`),
    [history, id, route.url]
  );
  return (
    <CandidateCard
      disabled={stepIsDisabled}
      loading={data.loading}
      isSelected={isSelected}
      onSelect={onSelect}
      status={status}
      statusTooltip={statusTooltip}
      highlight={highlight}
      candidate={data}
      onSelectCandidate={handleSelectCandidate}
      actions={actions}
      tags={allChatbotTags}
      compact={compact}
      data-test={compact ? "match-compact" : "match-full"}
      bottomButton={
        step.name === "selected" &&
        isOfferLetterEnabled &&
        isRegistered &&
        (!lastOffer || R.propEq("status", "rejected", lastOffer || {}))
          ? {
              name: lastOffer
                ? t("opportunity:process.candidateDetails.offer.reSendOffer")
                : t("opportunity:process.candidateDetails.offer.sendOffer"),
              icon: lastOffer ? "reload" : "file",
              onClick: showOffer,
              "data-test": "open-send-offer-modal-btn"
            }
          : undefined
      }
    />
  );
};

export default connect(({ company, opportunity }) => ({
  company,
  opportunity
}))(ProcessCard);
